import { Badge, Box, Flex, Text } from "@chakra-ui/react";
import { Wifi, AlertTriangle, Plane, Map } from "lucide-react";
import type { FleetDroneState } from "@/hooks/useFleetSocket";
import { useGetFleetStatusEndpointQuery } from "@/store";

export interface FleetStatusBarProps {
  connected: boolean;
  drones: Map<string, FleetDroneState>;
  error: string | null;
}

const POLL_INTERVAL_MS = 5000;

export function FleetStatusBar({ connected, drones, error }: FleetStatusBarProps) {
  const { data: fleetStatus } = useGetFleetStatusEndpointQuery(undefined, {
    pollingInterval: POLL_INTERVAL_MS,
  });

  const totalDrones = fleetStatus?.totalDrones ?? 0;
  const connectedDrones = fleetStatus?.connectedDrones ?? 0;
  const dronesInView = drones.size;

  const connectionStatus = connected ? "connected" : "disconnected";
  const connectionStatusColor = connected ? "green.500" : "red.500";

  return (
    <Flex
      align="center"
      justify="space-between"
      px="4"
      py="2"
      bg="bg.subtle"
      borderBottomWidth="1px"
      borderColor="border"
    >
      <Flex align="center" gap="4">
        {/* Total drones */}
        <Flex align="center" gap="2">
          <Plane style={{ height: 16, width: 16, opacity: 0.6 }} />
          <Text fontSize="sm">
            <Text as="span" fontWeight="semibold">{totalDrones}</Text>
            <Text as="span" color="fg.muted"> drones</Text>
          </Text>
        </Flex>

        {/* Connected */}
        <Flex align="center" gap="2">
          <Wifi style={{ height: 16, width: 16, color: "#22c55e" }} />
          <Text fontSize="sm">
            <Text as="span" fontWeight="semibold" color="green.500">{connectedDrones}</Text>
            <Text as="span" color="fg.muted"> online</Text>
          </Text>
        </Flex>

        {/* Drones in view */}
        <Flex align="center" gap="2">
          <Map style={{ height: 16, width: 16, opacity: 0.6 }} />
          <Text fontSize="sm">
            <Text as="span" fontWeight="semibold">{dronesInView}</Text>
            <Text as="span" color="fg.muted"> in view</Text>
          </Text>
        </Flex>
      </Flex>

      <Flex align="center" gap="4">
        {error && (
          <Badge colorPalette="red">
            <Flex align="center" gap="1">
              <AlertTriangle style={{ height: 12, width: 12 }} />
              {error}
            </Flex>
          </Badge>
        )}

        {/* Connection status */}
        <Flex align="center" gap="2">
          <Box
            h="2"
            w="2"
            borderRadius="full"
            bg={connectionStatusColor}
            css={{
              animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.5 },
              },
            }}
          />
          <Text fontSize="sm" color="fg.muted" textTransform="capitalize">
            {connectionStatus}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
}
